"use client";

import { useState, useEffect } from "react";
import { ShoppingCart, Menu, X, ChevronDown, ChevronRight, User, Settings, LogOut } from "lucide-react";
import SearchDropdown from "./search-dropdown";
import { useRouter } from "next/navigation";
import { useCart } from "@/hooks/use-cart";
import { useAuth } from "@/hooks/use-auth";
import VerificationBanner from "./verification-banner";
import {
  fetchAllCategories,
  fetchAllBrands,
  fetchSubcategories,
} from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// TypeScript interfaces for mock components
interface ButtonProps {
  children: React.ReactNode;
  variant?: "default" | "ghost";
  size?: "default" | "sm";
  className?: string;
  onClick?: () => void;
  [key: string]: any;
}

interface InputProps {
  className?: string;
  [key: string]: any;
}

interface LinkProps {
  href: string;
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}

// Mock components for demonstration
const Button = ({
  children,
  variant = "default",
  size = "default",
  className = "",
  ...props
}: ButtonProps) => (
  <button
    className={`inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background
      ${
        variant === "ghost"
          ? "hover:bg-accent hover:text-accent-foreground"
          : "bg-primary text-primary-foreground hover:bg-primary/90"
      }
      ${size === "sm" ? "h-9 px-3 text-sm" : "h-10 px-4 py-2"}
      ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Input = ({ className = "", ...props }: InputProps) => (
  <input
    className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

const Link = ({ href, children, className = "", ...props }: LinkProps) => (
  <a href={href} className={className} {...props}>
    {children}
  </a>
);

interface Category {
  id: number;
  name: string;
  href: string;
  subcategories: Array<{ category: string; href: string }>;
}

interface Brand {
  id: number;
  name: string;
  href: string;
}

interface Condition {
  id: number;
  name: string;
  href: string;
}

interface MegaMenuSection {
  title: string;
  items: Array<{ name: string; href: string }>;
}

export default function Header() {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileAccordionOpen, setMobileAccordionOpen] = useState<string | null>(
    null
  );
  const router = useRouter();
  const { getTotalItems } = useCart();
  const { state: authState, logout } = useAuth()
  const { isAuthenticated, user } = authState
  // const { user, isAuthenticated, logout } = useAuth();

  // Fetch categories, brands, and conditions from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [categoriesData, brandsData] = await Promise.all([
          fetchAllCategories(),
          fetchAllBrands(),
        ]);

        // Map categories data - handle paginated response
        const mappedCategories = (categoriesData.data || []).map(
          (cat: any) => ({
            id: cat.id,
            name: cat.category || cat.name || "",
            href: `/category/${cat.id}`,
            subcategories: cat.subcategories || [],
          })
        );

        // Map brands data - handle paginated response
        const mappedBrands = (brandsData.data || []).map((brand: any) => ({
          id: brand.id,
          name: brand.brand || brand.name || "",
          href: `/brand/${brand.id}`,
        }));

        setCategories(mappedCategories);
        setBrands(mappedBrands);
        // Note: Conditions endpoint doesn't exist on backend, so we'll use static data for now
        setConditions([
          { id: 1, name: "Aches & Pains", href: "/conditions/aches-pains" },
          { id: 2, name: "Cold, Flu and Cough", href: "/conditions/cold-flu" },
          {
            id: 3,
            name: "Anti Infectives",
            href: "/conditions/anti-infectives",
          },
          { id: 4, name: "Bites & Stings", href: "/conditions/bites-stings" },
          { id: 5, name: "Allergy & Hayfever", href: "/conditions/allergy" },
          { id: 6, name: "Eye Care", href: "/conditions/eye-care" },
          { id: 7, name: "Oral Care", href: "/conditions/oral-care" },
          { id: 8, name: "Womens Health", href: "/conditions/womens-health" },
          { id: 9, name: "Sexual Health", href: "/conditions/sexual-health" },
          {
            id: 10,
            name: "Skin Treatments",
            href: "/conditions/skin-treatments",
          },
          {
            id: 11,
            name: "First Aid & Bandages",
            href: "/conditions/first-aid",
          },
          {
            id: 12,
            name: "Equipment & Diagnostics",
            href: "/conditions/equipment",
          },
        ]);
      } catch (error) {
        console.error("Failed to fetch navigation data:", error);
        // Fallback to static data if API fails
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Create dynamic condition mega menu from fetched conditions
  const createConditionMegaMenu = () => {
    if (conditions.length === 0) return [];

    // Group conditions into categories (you can customize this logic)
    const commonConditions = conditions.slice(0, 5);
    const specializedCare = conditions.slice(5, 10);
    const emergencyEquipment = conditions.slice(10, 12);

    const sections = [];

    if (commonConditions.length > 0) {
      sections.push({
        title: "Common Conditions",
        items: commonConditions.map((condition) => ({
          name: condition.name,
          href: condition.href,
        })),
      });
    }

    if (specializedCare.length > 0) {
      sections.push({
        title: "Specialized Care",
        items: specializedCare.map((condition) => ({
          name: condition.name,
          href: condition.href,
        })),
      });
    }

    if (emergencyEquipment.length > 0) {
      sections.push({
        title: "Emergency & Equipment",
        items: emergencyEquipment.map((condition) => ({
          name: condition.name,
          href: condition.href,
        })),
      });
    }

    return sections;
  };

  const conditionMegaMenu = createConditionMegaMenu();

  // Create dynamic brand mega menu from fetched brands
  const createBrandMegaMenu = () => {
    if (brands.length === 0) return [];

    // Group brands into categories (you can customize this logic)
    const popularBrands = brands.slice(0, 5);
    const healthcareBrands = brands.slice(5, 9);
    const beautyBrands = brands.slice(9, 12);

    const sections = [];

    if (popularBrands.length > 0) {
      sections.push({
        title: "Popular Brands",
        items: popularBrands.map((brand) => ({
          name: brand.name,
          href: brand.href,
        })),
      });
    }

    if (healthcareBrands.length > 0) {
      sections.push({
        title: "Healthcare Brands",
        items: healthcareBrands.map((brand) => ({
          name: brand.name,
          href: brand.href,
        })),
      });
    }

    if (beautyBrands.length > 0) {
      sections.push({
        title: "Beauty Brands",
        items: beautyBrands.map((brand) => ({
          name: brand.name,
          href: brand.href,
        })),
      });
    }

    return sections;
  };

  const brandMegaMenu = createBrandMegaMenu();

  const handleCategoryClick = (category: string) => {
    const categoryId = categories.find((c) => c.name === category)?.id;
    if (categoryId) {
      router.push(`/category/${categoryId}`);
    }
  };

  const handleConditionClick = (condition: string) => {
    const conditionId = conditions.find((c) => c.name === condition)?.id;
    if (conditionId) {
      router.push(`/shop?condition=${conditionId}`);
    }
  };

  const handleBrandClick = (brand: string) => {
    const brandId = brands.find((b) => b.name === brand)?.id;
    if (brandId) {
      router.push(`/shop?brand=${brandId}`);
    }
  };

  const MegaMenuDropdown = ({
    menu,
    isOpen,
    onItemClick,
  }: {
    menu: MegaMenuSection[];
    isOpen: boolean;
    onItemClick: (name: string) => void;
  }) => {
    if (!isOpen) return null;

    return (
      <div className="absolute left-0 top-full bg-white border-t border-gray-200 shadow-xl z-50 w-max max-w-4xl max-h-96 overflow-y-auto animate-in fade-in-0 slide-in-from-top-2 duration-200">
        <div className="px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-6">
            {menu.map((section, index) => (
              <div key={index} className="min-w-48">
                <h3 className="font-semibold text-green-600 mb-4 text-lg">
                  {section.title}
                </h3>
                <div className="space-y-2">
                  {section.items.map((item, itemIndex) => (
                    <button
                      key={itemIndex}
                      onClick={() => onItemClick(item.name)}
                      className="block w-full text-left text-sm text-gray-700 hover:text-green-800 hover:bg-gray-50 p-2 rounded transition-colors duration-150"
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const CategoryDropdown = ({ isOpen }: { isOpen: boolean }) => {
    if (!isOpen) return null;

    return (
      <div className="absolute left-0 top-full bg-white border border-gray-200 shadow-xl z-50 w-80 animate-in fade-in-0 slide-in-from-top-2 duration-200">
        <div className="py-2">
          <button
            onClick={() => router.push("/shop")}
            className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:text-green-800 hover:bg-gray-50 border-b border-gray-100 font-medium"
          >
            All Products
          </button>
          {categories.map((category, index) => (
            <div
              key={index}
              className="relative"
              onMouseEnter={() => setHoveredCategory(category.name)}
              onMouseLeave={() => setHoveredCategory(null)}
            >
              <button
                onClick={() => handleCategoryClick(category.name)}
                className="flex items-center justify-between w-full px-4 py-3 text-sm text-gray-700 hover:text-green-800 hover:bg-gray-50 transition-colors duration-150"
              >
                {category.name}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>

              {/* Subcategory Dropdown */}
              {hoveredCategory === category.name && (
                <div className="absolute left-full top-0 bg-white border border-gray-200 shadow-xl z-50 w-[36rem] animate-in fade-in-0 slide-in-from-left-2 duration-200">
                  <div className="py-2">
                    <div className="px-4 py-2 text-xs font-semibold text-green-800 uppercase tracking-wide border-b border-gray-100">
                      {category.name}
                    </div>
                    <div className="grid grid-cols-3 gap-0">
                      {category.subcategories.map((subcategory, subIndex) => (
                        <button
                          key={subIndex}
                          onClick={() => router.push(`/category/${category.id}?subcategory=${subcategory.category}`)}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:text-green-800 hover:bg-gray-50 transition-colors duration-150"
                        >
                          {subcategory.category}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Mobile Menu Sidebar Component
  const MobileMenuSidebar = () => {
    if (!mobileMenuOpen) return null;

    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* Sidebar */}
        <div className="fixed left-0 top-0 h-full w-80 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out md:hidden">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Search Bar */}
            <div className="p-4 border-b border-gray-200">
              <SearchDropdown />
            </div>

            {/* Navigation Items */}
            <div className="flex-1 overflow-y-auto">
              {/* All Products */}
              <button
                onClick={() => {
                  router.push("/shop");
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:text-green-800 hover:bg-gray-50 border-b border-gray-100 font-medium"
              >
                All Products
              </button>

              {/* Categories Accordion */}
              <div className="border-b border-gray-200">
                <button
                  onClick={() =>
                    setMobileAccordionOpen(
                      mobileAccordionOpen === "categories" ? null : "categories"
                    )
                  }
                  className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:text-green-800 hover:bg-gray-50"
                >
                  <span className="font-medium">Categories</span>
                  {mobileAccordionOpen === "categories" ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                {mobileAccordionOpen === "categories" && (
                  <div className="bg-gray-50">
                    {categories.map((category, index) => (
                      <div key={index}>
                        <button
                          onClick={() => {
                            handleCategoryClick(category.name);
                            setMobileMenuOpen(false);
                          }}
                          className="w-full text-left px-6 py-2 text-sm text-gray-700 hover:text-green-800 hover:bg-gray-100"
                        >
                          {category.name}
                        </button>
                        {category.subcategories &&
                          category.subcategories.length > 0 && (
                            <div className="bg-gray-100">
                              {category.subcategories.map(
                                (subcategory, subIndex) => (
                                  <button
                                    key={subIndex}
                                    onClick={() => {
                                      router.push(`/category/${category.id}?subcategory=${subcategory.category}`);
                                      setMobileMenuOpen(false);
                                    }}
                                    className="w-full text-left px-8 py-1 text-xs text-gray-600 hover:text-green-800 hover:bg-gray-200"
                                  >
                                    {subcategory.category}
                                  </button>
                                )
                              )}
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Conditions Accordion */}
              <div className="border-b border-gray-200">
                <button
                  onClick={() =>
                    setMobileAccordionOpen(
                      mobileAccordionOpen === "conditions" ? null : "conditions"
                    )
                  }
                  className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:text-green-800 hover:bg-gray-50"
                >
                  <span className="font-medium">Conditions</span>
                  {mobileAccordionOpen === "conditions" ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                {mobileAccordionOpen === "conditions" && (
                  <div className="bg-gray-50">
                    {conditions.map((condition, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          handleConditionClick(condition.name);
                          setMobileMenuOpen(false);
                        }}
                        className="w-full text-left px-6 py-2 text-sm text-gray-700 hover:text-green-800 hover:bg-gray-100"
                      >
                        {condition.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Brands Accordion */}
              <div className="border-b border-gray-200">
                <button
                  onClick={() =>
                    setMobileAccordionOpen(
                      mobileAccordionOpen === "brands" ? null : "brands"
                    )
                  }
                  className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:text-green-800 hover:bg-gray-50"
                >
                  <span className="font-medium">Brands</span>
                  {mobileAccordionOpen === "brands" ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                {mobileAccordionOpen === "brands" && (
                  <div className="bg-gray-50">
                    {brands.map((brand, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          handleBrandClick(brand.name);
                          setMobileMenuOpen(false);
                        }}
                        className="w-full text-left px-6 py-2 text-sm text-gray-700 hover:text-green-800 hover:bg-gray-100"
                      >
                        {brand.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Link
                href="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm text-gray-700 hover:text-green-800 hover:bg-gray-50 border-b border-gray-200"
              >
                Contact Us
              </Link>


              <Link
                href="https://wa.me/254790682161"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 border-b border-gray-200"
              >
                Submit Prescription
              </Link>



              <Link
                href="/contact-us"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm text-gray-700 hover:text-green-800 hover:bg-gray-50 border-b border-gray-200"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <VerificationBanner />
      {/* Top Bar */}
      <div className="bg-green-900 text-white py-2">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row justify-between items-center text-sm">
            <div className="hidden sm:flex items-center gap-6">
              <span className="hover:underline">
                Health | Consultation | Prescription
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* <span>Free Delivery For orders above 2,500/=.</span> */}

              <span>
                Consult a Specialist +254790682161/+254789553015
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden p-2 text-gray-600 hover:text-gray-800"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Logo */}
          <Link href="/" className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="Qpharm Logo"
              style={{ maxWidth: "180px", height: "auto" }}
            />
          </Link>

          {/* Search Bar */}
          <div className="hidden md:flex flex-1 max-w-2xl mx-8 relative">
            <SearchDropdown />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4">
            <Link
              href="https://wa.me/254790682161"
              className="hidden sm:flex items-center gap-2 text-green-600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-6 h-6"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              <span className="font-medium">+254790682161</span>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              className="relative"
              onClick={() => router.push("/cart")}
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {getTotalItems()}
              </span>
            </Button>
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">
                      {user?.name?.split(" ")[0] || "Account"}
                    </span>
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => router.push("/dashboard")}>
                    <User className="mr-2 h-4 w-4" />
                    <span>Dashboard</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => router.push("/profile")}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Profile Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => router.push("/orders")}>
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    <span>My Orders</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => router.push("/login")}
                >
                  Login
                </Button>
                <Button
                  size="sm"
                  onClick={() => router.push("/register")}
                >
                  Register
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Search Bar */}
      <div className="md:hidden border-t border-gray-200 bg-white px-4 py-3">
        <SearchDropdown />
      </div>

      {/* Navigation Menu */}
      <div className="hidden md:block border-t border-b bg-white relative">
        <div className="container mx-auto px-4">
          <nav className="flex">
            {/* Shop By Category - Nested Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setOpenMenu("category")}
              onMouseLeave={() => {
                setOpenMenu(null);
                setHoveredCategory(null);
              }}
            >
              <button className="h-12 px-6 text-gray-700 hover:text-green-800 hover:bg-gray-50 flex items-center transition-colors duration-150">
                Shop By Category
                <svg
                  className="ml-1 w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <CategoryDropdown isOpen={openMenu === "category"} />
            </div>

            {/* Shop By Condition */}
            <div
              className="relative"
              onMouseEnter={() => setOpenMenu("condition")}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <button className="h-12 px-6 text-gray-700 hover:text-green-800 hover:bg-gray-50 flex items-center transition-colors duration-150">
                Shop By Condition
                <svg
                  className="ml-1 w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <MegaMenuDropdown
                menu={conditionMegaMenu}
                isOpen={openMenu === "condition"}
                onItemClick={handleConditionClick}
              />
            </div>

            {/* Shop By Brand */}
            <div
              className="relative"
              onMouseEnter={() => setOpenMenu("brand")}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <button className="h-12 px-6 text-gray-700 hover:text-green-800 hover:bg-gray-50 flex items-center transition-colors duration-150">
                Shop By Brand
                <svg
                  className="ml-1 w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <MegaMenuDropdown
                menu={brandMegaMenu}
                isOpen={openMenu === "brand"}
                onItemClick={handleBrandClick}
              />
            </div>

            {/* Other Navigation Items */}

            <Link
              href="/shop"
              className="h-12 px-6 text-black-600 hover:text-black-700 hover:bg-black-50 flex items-center gap-1 transition-colors duration-150"
              rel="noopener noreferrer"
            >
              Shop
            </Link>
           
            <Link
              href="https://wa.me/254790682161"
              className="h-12 px-6 text-green-600 hover:text-green-700 hover:bg-green-50 flex items-center gap-1 transition-colors duration-150"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-4 h-4"
              >
                <path d="M9 12h6"></path>
                <path d="M12 9v6"></path>
                <path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z"></path>
              </svg>
              Submit Prescription (WhatsApp)
            </Link>
            <Link
              href="/contact"
              className="h-12 px-6 text-black-600 hover:text-black-700 hover:bg-black-50 flex items-center gap-1 transition-colors duration-150"
              rel="noopener noreferrer"
            >
              Contact Us
            </Link>
          </nav>
        </div>
      </div>

      {/* Mobile Menu Sidebar */}
      <MobileMenuSidebar />
    </header>
  );
}